import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_URL } from "../../config";
import { showToast } from "../../components/toast";
import Swal from "sweetalert2";
import { useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPencilAlt,
  faCircleInfo,
  faArrowUp,
  faArrowDown,
  faPlus,
  faExchangeAlt,
  faEye,
  faDownload,
  faPaperPlane,
  faCheck,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { Link } from "react-router-dom";
import LoadingSpinner from "../../components/LoadingSpinner";
import { PermissionWrapper } from "../../helpers/permission";
import CareparvaResponseModal from "../notifications/CareparvaResponseModal.jsx";

const PortfolioDetails = () => {
  const { id } = useParams();
  const [portfolioDetails, setPortfolioDetails] = useState(null);
  const [portfolioStocks, setPortfolioStocks] = useState([]);
  const [rebalanceTimeline, setRebalanceTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({
    key: "",
    direction: "asc",
  });
  const [logModalData, setLogModalData] = useState(null);
  const [showLogModal, setShowLogModal] = useState(false);
  const [reasonModalData, setReasonModalData] = useState(null);
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [generatingFactsheet, setGeneratingFactsheet] = useState(false);
  const [sendingCareparrva, setSendingCareparrva] = useState(false);

  // Careparva Response Modal state
  const [selectedCareparvaNotification, setSelectedCareparvaNotification] = useState(null);
  const [careparvaModalType, setCareparvaModalType] = useState("create");
  const [isCareparvaModalOpen, setIsCareparvaModalOpen] = useState(false);

  const handleOpenCareparvaModal = (timelineRow, type = "create") => {
    setSelectedCareparvaNotification({
      ...timelineRow,
      isPortfolio: true,
      portfolio_name: portfolioDetails?.service_name || "Portfolio",
      service_name: portfolioDetails?.service_name || "Portfolio Service",
      unique_id: `Timeline #${timelineRow?.id || portfolioDetails?.id || ""}`,
    });
    setCareparvaModalType(type);
    setIsCareparvaModalOpen(true);
  };

  const createTimelineRow =
    rebalanceTimeline.find((r) => Number(r.parva_create) > 0) ||
    (rebalanceTimeline.length > 0 ? rebalanceTimeline[rebalanceTimeline.length - 1] : null);

  const isParvaCreated =
    Number(portfolioDetails?.parva_create) === 1 ||
    rebalanceTimeline.some((r) => Number(r.parva_create) === 1);

  const isParvaFailed =
    !isParvaCreated &&
    (Number(portfolioDetails?.parva_create) === 2 ||
      rebalanceTimeline.some((r) => Number(r.parva_create) === 2));

  const handleOpenCreateModal = () => {
    const targetRow = createTimelineRow || {
      id: portfolioDetails?.id,
      parva_create: isParvaCreated ? 1 : isParvaFailed ? 2 : 0,
      parva_response: portfolioDetails?.parva_response,
    };
    handleOpenCareparvaModal(targetRow, "create");
  };

  // Fetch portfolio details on component mount
  useEffect(() => {
    const fetchPortfolioDetails = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("authToken");

        // First API call for portfolio details and messages
        const portfolioResponse = await axios.get(
          `${API_URL}portfolio/${id}/message`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        // Second API call for portfolio stocks and rebalance data
        const listingResponse = await axios.get(
          `${API_URL}portfolio/details/${id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        console.log("Portfolio Response:", portfolioResponse.data);
        console.log("Listing Response:", listingResponse.data);

        // Set data from first API call
        if (portfolioResponse.data && portfolioResponse.data.success) {
          setPortfolioDetails(portfolioResponse.data.data.service);
        }

        // Set data from second API call
        if (listingResponse.data) {
          if (listingResponse.data.service) {
            setPortfolioDetails((prev) => ({
              ...listingResponse.data.service,
              ...(prev || {}),
              factsheet_url: listingResponse.data.service.factsheet_url || prev?.factsheet_url,
            }));
          }

          // Use the data structure from your example
          if (
            listingResponse.data.portfolio &&
            Array.isArray(listingResponse.data.portfolio)
          ) {
            setPortfolioStocks(listingResponse.data.portfolio);
          }

          if (
            listingResponse.data.rebalanceTimelineArr &&
            Array.isArray(listingResponse.data.rebalanceTimelineArr)
          ) {
            setRebalanceTimeline(listingResponse.data.rebalanceTimelineArr);
          }
        }

        setLoading(false);
      } catch (err) {
        console.error("Error fetching portfolio details:", err);
        showToast.error("Failed to load portfolio details");
        setLoading(false);
      } finally {
        setLoading(false);
      }
    };

    fetchPortfolioDetails();
  }, [id]);

  const handleViewReason = (stock) => {
    // stock contains add_reason & delete_reason
    setReasonModalData(stock);
    setShowReasonModal(true);
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }

    setSortConfig({ key, direction });

    // Sort the portfolio stocks
    const sortedStocks = [...portfolioStocks].sort((a, b) => {
      // Handle numeric values
      if (
        key === "weightage" ||
        key === "price" ||
        key === "last_price" ||
        key === "initial_price" ||
        key === "change_percent" ||
        key === "one_day_change"
      ) {
        const aValue = parseFloat(a[key]) || 0;
        const bValue = parseFloat(b[key]) || 0;
        return direction === "asc" ? aValue - bValue : bValue - aValue;
      }

      // Handle string values
      if (a[key] < b[key]) {
        return direction === "asc" ? -1 : 1;
      }
      if (a[key] > b[key]) {
        return direction === "asc" ? 1 : -1;
      }
      return 0;
    });

    setPortfolioStocks(sortedStocks);
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return faExchangeAlt;
    return sortConfig.direction === "asc" ? faArrowUp : faArrowDown;
  };

  const handleLogView = async (logId) => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      const response = await axios.get(
        `${API_URL}portfolio/rebalance-timeline-sub?timelineid=${logId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data && response.data.success) {
        setLogModalData(response.data.data);
        setShowLogModal(true);
      } else {
        showToast.error("No sub-timeline data found");
      }
    } catch (err) {
      console.error("Error fetching log details:", err);
      showToast.error("Failed to load log details");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`; // DD-MM-YYYY format
  };

  const formatCurrency = (amount) => {
    if (!amount) return "0.00";
    return new Intl.NumberFormat("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const handleExportRebalanceTimeline = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      const response = await axios.get(`${API_URL}portfolio/exportLog/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob", // important to download file
      });

      // Create blob link to download
      const blob = new Blob([response.data], {
        type: response.headers["content-type"],
      });
      const url = window.URL.createObjectURL(blob);

      // Create a temporary <a> element to trigger download
      const link = document.createElement("a");
      link.href = url;

      // You can customize filename using Content-Disposition header if API sends it
      const contentDisposition = response.headers["content-disposition"];
      let fileName = "PortfolioLog.xlsx"; // default
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match && match[1]) {
          fileName = match[1];
        }
      }

      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();

      // Cleanup
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export failed:", err);
      showToast.error("Failed to export log");
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");

      // 🔹 Send API request for export
      const response = await axios.get(`${API_URL}portfolio/export/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "*/*",
        },
        responseType: "blob", // important to download Excel/CSV
      });

      // 🔹 Create a blob and trigger the download
      const blob = new Blob([response.data], {
        type: response.headers["content-type"],
      });
      const url = window.URL.createObjectURL(blob);

      // 🔹 Create a temporary <a> tag to trigger download
      const link = document.createElement("a");
      link.href = url;

      // 🔹 Extract filename from Content-Disposition header, fallback default
      const contentDisposition = response.headers["content-disposition"];
      let fileName = `${portfolioDetails.service_name}_Export.xlsx`; // default name
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match && match[1]) fileName = match[1];
      }

      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();

      // 🔹 Clean up
      link.remove();
      window.URL.revokeObjectURL(url);

      showToast.success("Portfolio exported successfully!");
    } catch (err) {
      console.error("Export failed:", err);
      showToast.error("Failed to export portfolio data");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  const handleDownloadFactsheet = async (portfolioId, existingFactsheetUrl) => {
    try {
      let factsheetUrl = existingFactsheetUrl || portfolioDetails?.factsheet_url;

      if (!factsheetUrl) {
        const token = localStorage.getItem("authToken");

        // 🔹 Fetch portfolio details from API
        const response = await axios.get(
          `${API_URL}portfolio/details/${portfolioId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: "*/*",
            },
          }
        );

        factsheetUrl = response.data?.service?.factsheet_url;
      }

      // 🔹 Validate URL
      if (!factsheetUrl) {
        showToast.error("Factsheet not available for this portfolio.");
        return;
      }

      // 🔹 Open the factsheet link from database
      window.open(factsheetUrl, "_blank", "noopener,noreferrer");
    } catch (error) {
      console.error("Error downloading factsheet:", error);
      showToast.error("Failed to download factsheet");
    }
  };

  const handleGenerateFactsheet = async (serviceId) => {
    try {
      setGeneratingFactsheet(true);
      const token = localStorage.getItem("authToken");

      // 🔹 Call generate factsheet API endpoint
      const response = await axios.get(
        `${API_URL}portfolio/generate-factsheet/${serviceId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      // 🔹 Check response for success
      if (response.data && response.data.success) {
        showToast.success(response.data.message || "Factsheet generated successfully!");
      } else {
        showToast.error(response.data?.message || "Failed to generate factsheet");
      }
    } catch (error) {
      console.error("Error generating factsheet:", error);
      showToast.error(
        error.response?.data?.message || "Failed to generate factsheet"
      );
    } finally {
      setGeneratingFactsheet(false);
    }
  };

  const handleSendCareparrva = async () => {
    if (!portfolioDetails?.id) return;
    const serviceName = portfolioDetails.service_name || "Portfolio";
    const confirmResult = await Swal.fire({
      title: "Send to Careparrva?",
      text: `Are you sure you want to send "${serviceName}" portfolio advice to Careparrva PDC?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#6f42c1",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, Send!",
    });

    if (!confirmResult.isConfirmed) return;

    try {
      setSendingCareparrva(true);
      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        `${API_URL}portfolio/sendCareparrvaPortfolio/${portfolioDetails.id}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data && response.data.success) {
        Swal.fire("Success!", response.data.message || "Portfolio advice sent to Careparrva successfully", "success");
        showToast.success("Portfolio advice sent to Careparrva successfully");
      } else {
        Swal.fire("Failed", response.data?.message || response.data?.error || "Failed to send portfolio to Careparrva", "error");
        showToast.error(response.data?.message || "Failed to send portfolio to Careparrva");
      }
    } catch (err) {
      console.error("Error sending portfolio to Careparrva:", err);
      const errorMsg = err.response?.data?.message || err.message || "Failed to send portfolio to Careparrva";
      Swal.fire("Error", typeof errorMsg === "object" ? JSON.stringify(errorMsg) : errorMsg, "error");
      showToast.error(typeof errorMsg === "object" ? JSON.stringify(errorMsg) : errorMsg);
    } finally {
      setSendingCareparrva(false);
    }
  };

  if (!portfolioDetails) {
    return <div>Portfolio not found</div>;
  }

  return (
    <div
      id="page-container"
      className="page-header-fixed page-sidebar-fixed fade in"
    >
      {/* Header would be included via your layout */}

      <div id="content" className="">
        {/* Toast notifications would appear here */}

        <div className="row pb-2">
          <div className="col-md-6">
            <ul className="breadcrumb">
              <li className="breadcrumb-item">
                <a href="/Dashboard">
                  <i className="ti-home"></i> Home
                </a>
              </li>
              <li className="breadcrumb-item active">Manage Portfolio</li>
            </ul>
          </div>
          <div className="col-md-6" style={{ textAlign: "right" }}>
            <PermissionWrapper module="portfoliomanage" action="add">
              <Link
                to={`/research/Portfolio/edit/${id}`}
                className="btn btn-default btn-sm pull-right"
              >
                <FontAwesomeIcon icon={faPlus} /> Manage Portfolio
              </Link>
            </PermissionWrapper>
          </div>
        </div>

        <div className="panel panel-default with-rounded-corner with-shadow">
          <div className="panel-heading d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center gap-2">
              <span className="panel-title mb-0">Portfolio Details</span>
              {isParvaCreated ? (
                <button
                  type="button"
                  onClick={handleOpenCreateModal}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    padding: "3px 10px",
                    borderRadius: "6px",
                    border: "1px solid #86efac",
                    background: "#f0fdf4",
                    color: "#16a34a",
                    fontSize: "12px",
                    fontWeight: "600",
                    cursor: "pointer",
                  }}
                  title="Careparva Advice Created (Click to view)"
                >
                  <FontAwesomeIcon icon={faCheck} /> Created
                </button>
              ) : isParvaFailed ? (
                <button
                  type="button"
                  onClick={handleOpenCreateModal}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    padding: "3px 10px",
                    borderRadius: "6px",
                    border: "1px solid #fca5a5",
                    background: "#fef2f2",
                    color: "#dc2626",
                    fontSize: "12px",
                    fontWeight: "600",
                    cursor: "pointer",
                  }}
                  title="Careparva Advice Failed (Click to view)"
                >
                  <FontAwesomeIcon icon={faXmark} /> Create Failed
                </button>
              ) : null}
            </div>

            <div className="d-flex gap-2">
              {/* ✅ Send to Careparrva Button */}
              {/* <button
                className="btn btn-sm"
                onClick={handleSendCareparrva}
                disabled={sendingCareparrva}
                style={{ backgroundColor: "#6f42c1", color: "#fff", borderColor: "#6f42c1" }}
              >
                {sendingCareparrva ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Sending...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faPaperPlane} /> Send to Careparrva
                  </>
                )}
              </button> */}

              {/* ✅ Generate Factsheet Button */}
              <button
                className="btn btn-info btn-sm"
                onClick={() => handleGenerateFactsheet(portfolioDetails.id)}
                disabled={generatingFactsheet}
              >
                {generatingFactsheet ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Generating...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faDownload} /> Generate Factsheet
                  </>
                )}
              </button>

              {/* ✅ Download Factsheet Button */}
              <button
                className="btn btn-primary btn-sm"
                onClick={() => handleDownloadFactsheet(portfolioDetails?.id, portfolioDetails?.factsheet_url)}
              >
                <FontAwesomeIcon icon={faDownload} /> Download Factsheet
              </button>
            </div>
          </div>

          <div className="panel-body">
            <div className="row">
              <div className="col-md-4 mb-3">
                <span className="fw-bold">Portfolio Type: </span>
                <span className="ms-2">{portfolioDetails.segment_name}</span>
              </div>
              <div className="col-md-4 mb-3">
                <span className="fw-bold">Portfolio Name: </span>
                <span className="ms-2">{portfolioDetails.service_name}</span>
              </div>
              <div className="col-md-4 mb-3">
                <span className="fw-bold">Investment Horizon: </span>
                <span className="ms-2">
                  {portfolioDetails.investment_period || "N/A"}
                </span>
              </div>
              <div className="col-md-4 mb-3">
                <span className="fw-bold">Rebalance Frequency: </span>
                <span className="ms-2">
                  {portfolioDetails.rebalance_frequency}
                </span>
              </div>
              <div className="col-md-4 mb-3">
                <span className="fw-bold">Asset Class: </span>
                <span className="ms-2">
                  {portfolioDetails.asset_name || "N/A"}
                </span>
              </div>
              <div className="col-md-4 mb-3">
                <span className="fw-bold">Volatility: </span>
                <span className="ms-2">{portfolioDetails.volatility}</span>
              </div>
              <div className="col-md-4 mb-3">
                <span className="fw-bold">Last Rebalance Date: </span>
                <span className="ms-2">
                  {portfolioDetails.last_rebalance_date
                    ? (() => {
                      const d = new Date(
                        portfolioDetails.last_rebalance_date
                      );
                      const day = String(d.getDate()).padStart(2, "0");
                      const month = String(d.getMonth() + 1).padStart(2, "0");
                      const year = d.getFullYear();
                      return `${day}-${month}-${year}`;
                    })()
                    : "N/A"}
                </span>
              </div>
              <div className="col-md-4 mb-3">
                <span className="fw-bold">Next Rebalance Date: </span>
                <span className="ms-2">
                  {portfolioDetails.next_rebalance_date
                    ? (() => {
                      const d = new Date(
                        portfolioDetails.next_rebalance_date
                      );
                      const day = String(d.getDate()).padStart(2, "0");
                      const month = String(d.getMonth() + 1).padStart(2, "0");
                      const year = d.getFullYear();
                      return `${day}-${month}-${year}`;
                    })()
                    : "N/A"}
                </span>
              </div>
              <div className="col-md-4 mb-3">
                <span className="fw-bold">Min Investment: </span>
                <span className="ms-2">
                  {formatCurrency(portfolioDetails.min_investment)}
                </span>
              </div>
              <div className="col-md-4 mb-3">
                <span className="fw-bold">CAGR: </span>
                <span className="ms-2">{portfolioDetails.cagr || "N/A"}</span>
              </div>
              <div className="col-md-4 mb-3">
                <span className="fw-bold">Launch Date: </span>
                <span className="ms-2">
                  {portfolioDetails.launch_date
                    ? new Date(
                      portfolioDetails.launch_date
                    ).toLocaleDateString()
                    : "N/A"}
                </span>
              </div>
              <div className="col-md-4 mb-3">
                <span className="fw-bold">Constituents: </span>
                <span className="ms-2">
                  {portfolioDetails.constituents || portfolioStocks.length}
                </span>
              </div>
              <div className="col-md-4 mb-3">
                <span className="fw-bold">Careparva Advice: </span>
                <span className="ms-2">
                  {isParvaCreated ? (
                    <button
                      type="button"
                      onClick={handleOpenCreateModal}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "5px",
                        padding: "2px 8px",
                        borderRadius: "5px",
                        border: "1px solid #86efac",
                        background: "#f0fdf4",
                        color: "#16a34a",
                        fontSize: "12px",
                        fontWeight: "600",
                        cursor: "pointer",
                      }}
                      title="Careparva Advice Created (Click to view)"
                    >
                      <FontAwesomeIcon icon={faCheck} /> Created
                    </button>
                  ) : isParvaFailed ? (
                    <button
                      type="button"
                      onClick={handleOpenCreateModal}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "5px",
                        padding: "2px 8px",
                        borderRadius: "5px",
                        border: "1px solid #fca5a5",
                        background: "#fef2f2",
                        color: "#dc2626",
                        fontSize: "12px",
                        fontWeight: "600",
                        cursor: "pointer",
                      }}
                      title="Careparva Advice Failed (Click to view)"
                    >
                      <FontAwesomeIcon icon={faXmark} /> Failed
                    </button>
                  ) : (
                    <span style={{ color: "#94a3b8", fontSize: "13px" }}>Not Created</span>
                  )}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-md-12" style={{ textAlign: "right" }}>
            <button
              onClick={handleExport}
              className="btn btn-default btn-sm pull-right mb-2"
            >
              <FontAwesomeIcon icon={faPlus} /> Export
            </button>
          </div>
        </div>

        <div className="table-responsive panel panel-default with-rounded-corner with-shadow p-20 tbl_head_sticky">
          <table className="table">
            <thead>
              <tr>
                <th style={{ textAlign: "center", width: "4%" }}>#</th>
                <th
                  style={{ width: "12%", cursor: "pointer" }}
                  onClick={() => handleSort("stocks")}
                >
                  Symbol{" "}
                  <FontAwesomeIcon icon={getSortIcon("stocks")} size="xs" />
                </th>
                <th
                  style={{ width: "11%", cursor: "pointer" }}
                  onClick={() => handleSort("isin_number")}
                >
                  ISIN{" "}
                  <FontAwesomeIcon icon={getSortIcon("isin_number")} size="xs" />
                </th>
                <th
                  style={{ width: "9%", cursor: "pointer" }}
                  onClick={() => handleSort("weightage")}
                >
                  Weightage (%){" "}
                  <FontAwesomeIcon icon={getSortIcon("weightage")} size="xs" />
                </th>
                <th
                  style={{ width: "11%", cursor: "pointer" }}
                  onClick={() => handleSort("portfolio_segment")}
                >
                  Segment{" "}
                  <FontAwesomeIcon
                    icon={getSortIcon("portfolio_segment")}
                    size="xs"
                  />
                </th>
                <th style={{ width: "9%" }}>Asset Class</th>
                <th
                  style={{ width: "8%", cursor: "pointer" }}
                  onClick={() => handleSort("initial_price")}
                >
                  Enter Price{" "}
                  <FontAwesomeIcon
                    icon={getSortIcon("initial_price")}
                    size="xs"
                  />
                </th>
                <th
                  style={{ width: "8%", cursor: "pointer" }}
                  onClick={() => handleSort("price")}
                >
                  CMP{" "}
                  <FontAwesomeIcon icon={getSortIcon("price")} size="xs" />
                </th>
                <th
                  style={{ width: "9%", cursor: "pointer" }}
                  onClick={() => handleSort("change_percent")}
                >
                  Change (%){" "}
                  <FontAwesomeIcon
                    icon={getSortIcon("change_percent")}
                    size="xs"
                  />
                </th>
                <th
                  style={{ width: "10%", cursor: "pointer" }}
                  onClick={() => handleSort("one_day_change")}
                >
                  1D Change (%){" "}
                  <FontAwesomeIcon
                    icon={getSortIcon("one_day_change")}
                    size="xs"
                  />
                </th>
                <th style={{ width: "9%" }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {portfolioStocks.length > 0 ? (
                portfolioStocks.map((row, index) => (
                  <tr key={row.id || index}>
                    <td style={{ textAlign: "center" }}>{index + 1}</td>
                    <td><strong>{row.stocks}</strong></td>
                    <td>{row.isin_number || "-"}</td>
                    <td>{row.weightage}</td>
                    <td>{row.portfolio_segment}</td>
                    <td>{row.asset_name || "N/A"}</td>
                    <td>{row.initial_price || ""}</td>
                    <td>{row.price || ""}</td>
                    <td
                      style={{
                        color:
                          parseFloat(row.change_percent) >= 0 ? "green" : "red",
                        fontWeight: "bold",
                      }}
                    >
                      {row.change_percent ? `${row.change_percent}%` : ""}
                    </td>
                    <td
                      style={{
                        color:
                          parseFloat(row.one_day_change) >= 0 ? "green" : "red",
                        fontWeight: "bold",
                      }}
                    >
                      {row.one_day_change !== null &&
                      row.one_day_change !== undefined &&
                      row.one_day_change !== ""
                        ? `${row.one_day_change}%`
                        : "-"}
                    </td>
                    <td>
                      <Link
                        to={`/research/Portfolio/rationaleinfo/${row.id}`}
                        className="btn btn-primary btn-sm"
                        title="Edit Rationale"
                      >
                        <FontAwesomeIcon icon={faPencilAlt} />
                      </Link>
                      {(row.add_reason || row.delete_reason) && (
                        <button
                          onClick={() => handleViewReason(row)}
                          className="btn btn-info btn-sm ms-1"
                          title="View Reason"
                        >
                          <FontAwesomeIcon icon={faEye} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="11" className="noRecord">
                    No Records Found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {rebalanceTimeline.length > 0 && (
          <>
            <div className="row">
              <div className="col-md-12" style={{ textAlign: "right" }}>
                <button
                  onClick={handleExportRebalanceTimeline}
                  className="btn btn-default btn-sm pull-right mb-2"
                >
                  <FontAwesomeIcon icon={faPlus} /> Export
                </button>
              </div>
            </div>
            <div className="table-responsive panel panel-default with-rounded-corner with-shadow p-20 tbl_head_sticky">
              <table className="table">
                <thead>
                  <tr align="center">
                    <th colSpan="4">Rebalance Timeline</th>
                  </tr>
                  <tr>
                    <th width="8%">#</th>
                    <th width="22%">Date</th>
                    <th width="50%">Message</th>
                    <th width="20%">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {rebalanceTimeline.map((row, index) => (
                    <tr key={row.id || index}>
                      <td>{index + 1}</td>
                      <td>{formatDate(row.datetime)}</td>
                      <td dangerouslySetInnerHTML={{ __html: row.message }} />
                      <td>
                        <div
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "6px",
                          }}
                        >
                          <button
                            onClick={() => handleLogView(row.id)}
                            className="btn btn-primary btn-sm logview"
                            title="View Log"
                          >
                            <FontAwesomeIcon icon={faCircleInfo} />
                          </button>

                          {/* Check Button: Update Advice */}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenCareparvaModal(row, "update");
                            }}
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              width: "28px",
                              height: "28px",
                              borderRadius: "6px",
                              border: `1px solid ${
                                Number(row.parva_update) === 2
                                  ? "#fca5a5"
                                  : Number(row.parva_update) === 1 ||
                                    Number(row.parva_update) > 0
                                  ? "#86efac"
                                  : "#e2e8f0"
                              }`,
                              background:
                                Number(row.parva_update) === 2
                                  ? "#fef2f2"
                                  : Number(row.parva_update) === 1 ||
                                    Number(row.parva_update) > 0
                                  ? "#f0fdf4"
                                  : "#f8fafc",
                              cursor: "pointer",
                              padding: 0,
                              transition: "all 0.15s ease",
                            }}
                            title="Update"
                          >
                            {Number(row.parva_update) === 1 ||
                            Number(row.parva_update) > 0 ? (
                              <FontAwesomeIcon
                                icon={faCheck}
                                style={{ color: "#16a34a", fontSize: "13px" }}
                              />
                            ) : Number(row.parva_update) === 2 ? (
                              <FontAwesomeIcon
                                icon={faXmark}
                                style={{
                                  color: "#dc2626",
                                  fontSize: "14px",
                                  fontWeight: "bold",
                                }}
                              />
                            ) : (
                              <FontAwesomeIcon
                                icon={faCheck}
                                style={{ color: "#cbd5e1", fontSize: "13px" }}
                              />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {portfolioDetails && (
          <div className="table-responsive panel panel-default with-rounded-corner with-shadow p-20 tbl_head_sticky">
            <table className="table">
              <tbody>
                <tr>
                  <th>
                    Last Rebalance Date :{" "}
                    {formatDate(portfolioDetails.last_rebalance_date)}
                  </th>
                  <th>
                    Next Rebalance Date :{" "}
                    {formatDate(portfolioDetails.next_rebalance_date)}
                  </th>
                  <th>
                    Minimum Investment :{" "}
                    {formatCurrency(portfolioDetails.min_investment)}
                  </th>
                  <th>CAGR : {portfolioDetails.cagr || "N/A"}</th>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Log Details Modal */}
      {showLogModal && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          tabIndex="-1"
          role="dialog"
          aria-labelledby="logModalLabel"
          aria-hidden="true"
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header justify-content-between py-0">
                <h5 className="modal-title" style={{ fontWeight: "bold" }}>
                  Log Details
                </h5>
                <button
                  type="button"
                  className="btn"
                  style={{ fontSize: "25px", border: "none" }}
                  onClick={() => setShowLogModal(false)}
                >
                  &times;
                </button>
              </div>
              <div className="modal-body" id="myModalContent">
                <div className="table-responsive panel panel-default with-rounded-corner with-shadow p-20 tbl_head_sticky">
                  <table className="table">
                    <thead>
                      <tr>
                        <th width="10%">#</th>
                        <th width="25%">Date</th>
                        <th width="30%">Constituents</th>
                        <th width="20%">Weightage</th>
                        <th width="15%">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logModalData && logModalData.length > 0 ? (
                        logModalData.map((item, index) => {
                          let statusText = "No Change";
                          if (item.status === 1)
                            statusText = (
                              <span className="text-success">Added</span>
                            );
                          else if (item.status === 2)
                            statusText = (
                              <span className="text-danger">Removed</span>
                            );
                          else if (item.status === 0) {
                            const val = parseFloat(item.per_change || "0");
                            if (!isNaN(val) && val !== 0) {
                              const text = `${val > 0 ? `▲ +${val}` : `▼ ${val}`}% Change`;
                              statusText = (
                                <span className={val > 0 ? "text-success" : "text-danger"}>
                                  {text}
                                </span>
                              );
                            }
                          }

                          return (
                            <tr key={item.id}>
                              <td>{index + 1}</td>
                              <td>
                                {item.created_on
                                  ? new Date(
                                    item.created_on
                                  ).toLocaleDateString("en-GB")
                                  : "N/A"}
                              </td>
                              <td>{item.constituents}</td>
                              <td>{item.weightage}</td>
                              <td>{statusText}</td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="5">No sub-timeline data found</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {showReasonModal && reasonModalData && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          tabIndex="-1"
          role="dialog"
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header d-flex justify-content-between">
                <h5 className="modal-title">Reason</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowReasonModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                {reasonModalData.add_reason && (
                  <div>
                    {/* <strong>Add Reason:</strong> */}
                    <div
                      dangerouslySetInnerHTML={{
                        __html: reasonModalData.add_reason,
                      }}
                    />
                  </div>
                )}
                {reasonModalData.delete_reason && (
                  <div>
                    <strong>Delete Reason:</strong>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: reasonModalData.delete_reason,
                      }}
                    />
                  </div>
                )}
                {!reasonModalData.add_reason &&
                  !reasonModalData.delete_reason && <p>No reason provided.</p>}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowReasonModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Careparva Response Modal */}
      <CareparvaResponseModal
        isOpen={isCareparvaModalOpen}
        onClose={() => setIsCareparvaModalOpen(false)}
        notification={selectedCareparvaNotification}
        modalType={careparvaModalType}
      />

      {/* Scroll to top button */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        data-click="scroll-top"
        className="btn-scroll-top fade"
        aria-label="Scroll to top"
      >
        <i className="ti-arrow-up"></i>
      </button>
    </div>
  );
};

export default PortfolioDetails;
